import React, { useState, useEffect } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { getConsent, hasDecided, setConsent } from '../../util/cookieConsent';

import css from './CookieConsent.module.css';

/**
 * GDPR / UK GDPR + PECR compliant cookie consent banner.
 *
 * - Non-essential cookies are OFF by default.
 * - "Accept all" and "Reject all" are rendered at equal visual prominence (ICO requirement).
 * - "Manage preferences" expander shows toggles, all defaulting to OFF.
 * - After a choice is saved the banner disappears and a persistent "Cookie settings"
 *   link renders so users can review / withdraw consent at any time.
 * - SSR-safe: returns null until mounted on the client.
 */
const CookieConsent = () => {
  const [mounted, setMounted] = useState(false);
  const [decided, setDecided] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    setMounted(true);
    const alreadyDecided = hasDecided();
    setDecided(alreadyDecided);
    if (!alreadyDecided) {
      setBannerOpen(true);
    }
  }, []);

  if (!mounted) return null;

  const openPreferences = () => {
    const consent = getConsent();
    setAnalyticsEnabled(consent?.analytics || false);
    setMarketingEnabled(consent?.marketing || false);
    setBannerOpen(true);
    setPrefsOpen(true);
  };

  const handleAcceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    setDecided(true);
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  const handleRejectAll = () => {
    setConsent({ analytics: false, marketing: false });
    setDecided(true);
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  const handleSavePreferences = () => {
    setConsent({ analytics: analyticsEnabled, marketing: marketingEnabled });
    setDecided(true);
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  return (
    <>
      {bannerOpen && (
        <div className={css.overlay} role="dialog" aria-modal="true" aria-label="Cookie consent">
          <div className={css.banner}>
            <p className={css.description}>
              <FormattedMessage
                id="CookieConsent.description"
                values={{
                  privacyLink: (
                    <a href="/p/privacy-policy" className={css.link}>
                      <FormattedMessage id="CookieConsent.privacyPolicy" />
                    </a>
                  ),
                }}
              />
            </p>

            <div className={css.actions}>
              <button className={css.acceptButton} onClick={handleAcceptAll}>
                <FormattedMessage id="CookieConsent.acceptAll" />
              </button>
              <button className={css.rejectButton} onClick={handleRejectAll}>
                <FormattedMessage id="CookieConsent.rejectAll" />
              </button>
              <button
                className={css.manageButton}
                onClick={() => setPrefsOpen(o => !o)}
                aria-expanded={prefsOpen}
                aria-label="Manage cookie preferences"
                title="Manage cookie preferences"
              >
                ⚙
              </button>
            </div>

            {prefsOpen && (
              <div className={css.preferences}>
                <div className={css.preferenceRow}>
                  <div className={css.preferenceText}>
                    <span className={css.preferenceName}>
                      <FormattedMessage id="CookieConsent.necessary" />
                    </span>
                    <span className={css.preferenceDesc}>
                      <FormattedMessage id="CookieConsent.necessaryDesc" />
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked
                    disabled
                    readOnly
                    aria-label="Necessary cookies — always on"
                    className={css.toggle}
                  />
                </div>

                <div className={css.preferenceRow}>
                  <div className={css.preferenceText}>
                    <span className={css.preferenceName}>
                      <FormattedMessage id="CookieConsent.analytics" />
                    </span>
                    <span className={css.preferenceDesc}>
                      <FormattedMessage id="CookieConsent.analyticsDesc" />
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={analyticsEnabled}
                    onChange={e => setAnalyticsEnabled(e.target.checked)}
                    aria-label="Analytics cookies"
                    className={css.toggle}
                  />
                </div>

                <div className={css.preferenceRow}>
                  <div className={css.preferenceText}>
                    <span className={css.preferenceName}>
                      <FormattedMessage id="CookieConsent.marketing" />
                    </span>
                    <span className={css.preferenceDesc}>
                      <FormattedMessage id="CookieConsent.marketingDesc" />
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={marketingEnabled}
                    onChange={e => setMarketingEnabled(e.target.checked)}
                    aria-label="Marketing cookies"
                    className={css.toggle}
                  />
                </div>

                <button className={css.saveButton} onClick={handleSavePreferences}>
                  <FormattedMessage id="CookieConsent.savePreferences" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {decided && !bannerOpen && (
        <button className={css.cookieSettingsLink} onClick={openPreferences}>
          <FormattedMessage id="CookieConsent.cookieSettings" />
        </button>
      )}
    </>
  );
};

export default CookieConsent;
